// src/pages/sow-draft/SalesforcePushModal.jsx
import { useState } from "react";
import PropTypes from "prop-types";
import { Cloud } from "lucide-react";
import { Alert, Button, FormField, Input, Modal } from "../../shared/ui";

// The real publish endpoint (POST /sow-drafts/{id}/publish/salesforce) has
// no connection-status pre-check and no document-type choice -- it always
// exports the draft's current content to .docx and pushes it to the linked
// SF Opportunity. The push button stays enabled (it calls a real, working
// route); only the status pre-check is unavailable.
export default function SalesforcePushModal({ open, onClose, currentVersion, sfOpportunityId, onPush }) {
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);

  const handlePush = async () => {
    setPushing(true);
    setResult(null);
    try {
      const res = await onPush();
      setResult(res);
    } catch (e) {
      setResult({ success: false, message: e.message || String(e) });
    } finally {
      setPushing(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Push to Salesforce"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handlePush} loading={pushing}>
            <Cloud size={14} /> Push to Salesforce
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="info">
          Connection status can't be pre-verified yet — pushing will attempt the export directly and
          report any failure below.
        </Alert>

        <FormField label="Salesforce Opportunity ID">
          <Input value={sfOpportunityId || "—"} disabled />
        </FormField>

        <FormField label="Document Version">
          <Input value={`v${currentVersion}`} disabled />
        </FormField>

        {result && (
          <Alert variant={result.success ? "success" : "danger"}>
            {result.message}
            {result.success && result.doc_name && (
              <div className="mt-1 text-xs">Document: {result.doc_name}</div>
            )}
          </Alert>
        )}
      </div>
    </Modal>
  );
}

SalesforcePushModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentVersion: PropTypes.string.isRequired,
  sfOpportunityId: PropTypes.string,
  onPush: PropTypes.func.isRequired,
};
